import { auth, functions, db } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { doc, updateDoc, runTransaction, serverTimestamp } from 'firebase/firestore';
import { buildFirstApprovedFieldsDotNotation, appendReportAuditEntry } from './reportAuditHelpers';

/**
 * Trigger cover page generation with a transactional guard.
 *
 * Uses `runTransaction` to atomically check whether the cover page is
 * already processing before setting `cpReportProcessingStatus = 'processing'`.
 * This prevents duplicate Cloud Function invocations when multiple callers
 * (e.g. a direct `TriggerCoverPage` from RD_DD/RD_PAD and a concurrent
 * `rerenderCoverPageIfStale` auto-helper) fire at the same time.
 *
 * Also writes `cpReportProcessingStartedAt` so the DocumentsDashboard
 * self-healing logic can detect and reset stuck statuses.
 *
 * The Cloud Function call is fire-and-forget: we return immediately with
 * `{ status: 'processing' }` and let the CF update Firestore on completion.
 * A `deadline-exceeded` error from the CF client is *not* treated as a real
 * failure — the CF continues running server-side and will update status
 * itself when done.
 */
export const TriggerCoverPage = async ({ clinicianId, patientId, reportType }) => {
  const createCoverPage = httpsCallable(functions, 'createCoverPage', {
    timeout: 360000, // 6 minutes — slightly above the server-side 5-min timeout
  });

  const user = auth.currentUser;
  if (!user || !user.uid) return { success: false, error: "Not authenticated" };

  if (!clinicianId || !patientId || !reportType) {
    console.error("Missing required parameters:", { clinicianId, patientId, reportType });
    return { success: false, error: "Missing required parameters" };
  }

  try {
    const patientDocRef = doc(db, 'patients', patientId);

    // Transactional guard: only one caller wins the race to set 'processing'.
    // If another caller already set it, we bail out early — no duplicate CF call.
    let alreadyProcessing = false;
    let manageReports = null;

    await runTransaction(db, async (transaction) => {
      const snap = await transaction.get(patientDocRef);
      manageReports = snap.data()?.dataModel?.['6_manageReports'];

      if (manageReports?.cpReportProcessingStatus === 'processing') {
        alreadyProcessing = true;
        return;
      }

      transaction.update(patientDocRef, {
        'dataModel.6_manageReports.cpReportProcessingStatus': 'processing',
        'dataModel.6_manageReports.cpReportProcessingStartedAt': serverTimestamp(),
        'dataModel.6_manageReports.cpReportApproved': true,
        'dataModel.6_manageReports.cpReportApprovedBy': clinicianId,
        'dataModel.6_manageReports.cpReportApprovedByName': user.displayName,
        'dataModel.6_manageReports.cpReportApprovedAt': serverTimestamp(),
        ...buildFirstApprovedFieldsDotNotation('cp', manageReports),
      });
    }, { maxAttempts: 10 });

    if (alreadyProcessing) {
      console.log('Skipping cover page render — already processing');
      return { success: true, data: { status: 'already-processing' } };
    }

    appendReportAuditEntry(patientId, {
      module: 'cp',
      eventType: 'approved',
      isFirstOccurrence: !manageReports?.cpReportFirstApprovedAt,
    });

    // Fire-and-forget: invoke the CF without awaiting its result.
    createCoverPage({
      clinicianId,
      patientId,
      reportType,
    }).then(result => {
      console.log('Cover page generated successfully:', result.data);
    }).catch(error => {
      console.error('Error in createCoverPage function:', error);

      // deadline-exceeded means the client timed out, but the CF is still
      // running server-side and will write 'complete' when done.  Don't
      // overwrite with 'error'.
      if (error.code === 'deadline-exceeded') {
        console.log('Client-side timeout occurred, but function continues processing in background');
        return;
      }

      updateDoc(patientDocRef, {
        'dataModel.6_manageReports.cpReportProcessingStatus': 'error',
      }).catch(updateError => {
        console.error('Error updating processing status:', updateError);
      });
    });

    return { success: true, data: { status: 'processing' } };

  } catch (error) {
    console.error('Error in TriggerCoverPage transaction:', error);
    return { success: false, error };
  }
}
